import { draftToMarkdown, markdownToDraft } from 'markdown-draft-js';

// ids are not persisted, only used for keying elements in the client
// so faster non-secure ids are fine
import { nanoid } from 'nanoid/non-secure';

import { getColorValue, getElementId, getElementType } from '../elements/utils/elementPropertyUtils';

import {
    COLOR_SWATCH_DEFAULT_HEIGHT_GRID,
    COLOR_SWATCH_DEFAULT_WIDTH_GRID,
} from '../colorSwatches/colorSwatchesConstants';

import { AiIdeaType, AIResponseElement, AiIdea } from './aiConversationTypes';
import { MNElement } from '../elements/elementModelTypes';

import { COLOR_SPACE } from '../colors/colorConstants';

import { PromptShortcut } from './aiPromptShortcutTypes';
import { AIConversationResultType } from '../../client/workspace/toolbar/components/elementTools/aiAssistantTool/state/aiToolStateConstants';
import {
    AiHistoryEntry,
    AiResultType,
} from '../../client/workspace/toolbar/components/elementTools/aiAssistantTool/state/aiToolStateTypes';
import { ElementType } from '../elements/elementTypes';
import { MNComment } from '../comments/commentTypes';

const formatIdeaObjectAsColorElement = (idea: AiIdea): AIResponseElement => {
    return {
        uuid: nanoid(),
        elementType: ElementType.COLOR_SWATCH_TYPE,
        content: {
            width: 29,
            media: {
                height: 10 * COLOR_SWATCH_DEFAULT_HEIGHT_GRID,
                width: 10 * COLOR_SWATCH_DEFAULT_WIDTH_GRID,
            },
            color: {
                space: COLOR_SPACE.HEX,
                value: idea.hex || '#000000',
            },
            showCaption: true,
            showColorValue: true,
            caption: markdownToDraft(idea.text || ''),
        },
    };
};

const formatIdeaObjectAsCardElement = (idea: AiIdea): AIResponseElement => {
    return {
        uuid: nanoid(),
        elementType: ElementType.CARD_TYPE,
        content: {
            textContent: markdownToDraft(idea.text || ''),
        },
    };
};

export const formatIdeaObjectAsElement = (idea: AiIdea): AIResponseElement | undefined => {
    switch (idea.type) {
        case AiIdeaType.FOLLOWUP:
            // followup ideas are not returned as elements
            return undefined;
        case AiIdeaType.COLOR:
            return formatIdeaObjectAsColorElement(idea);
        case AiIdeaType.TASK:
        case AiIdeaType.IDEA:
        default:
            return formatIdeaObjectAsCardElement(idea);
    }
};

const formatIdeaBotObjectAsHistoryEntry = (idea: AiIdea): AiHistoryEntry => ({
    uuid: nanoid(),
    elementType: AIConversationResultType.BOT_HISTORY,
    text: idea.text || '',
});

export const formatIdeaObjectForRender = (idea: AiIdea): AiResultType | undefined => {
    if (idea.type === AiIdeaType.OTHER) return formatIdeaBotObjectAsHistoryEntry(idea);

    return formatIdeaObjectAsElement(idea);
};

export const formatIdeaObjectAsPromptShortcut = (idea: AiIdea): PromptShortcut | undefined => {
    switch (idea.type) {
        case AiIdeaType.FOLLOWUP:
            return {
                id: idea.id || nanoid(),
                children: [],
                promptText: idea.text,
            };
        default:
            return undefined;
    }
};

export const canFormatElementAsIdeaObject = (element: MNElement): boolean => {
    switch (getElementType(element)) {
        case ElementType.COLOR_SWATCH_TYPE:
        case ElementType.IMAGE_TYPE:
        case ElementType.FILE_TYPE:
        case ElementType.LINK_TYPE:
        case ElementType.SKETCH_TYPE:
        case ElementType.BOARD_TYPE:
        case ElementType.COLUMN_TYPE:
        case ElementType.TASK_LIST_TYPE:
        case ElementType.CARD_TYPE:
        case ElementType.DOCUMENT_TYPE:
        case ElementType.TASK_TYPE:
            return true;
        default:
            return false;
    }
};

export const formatCommentAsIdeaObject = (comment: MNComment): AiIdea => {
    return {
        id: comment._id ? comment._id : undefined,
        type: AiIdeaType.IDEA,
        text: draftToMarkdown(comment.text),
    };
};

export const formatElementAsIdeaObject = (element: MNElement, includeId = false): AiIdea | undefined => {
    const id = includeId ? getElementId(element) : undefined;

    switch (getElementType(element)) {
        case ElementType.COLOR_SWATCH_TYPE:
            return {
                id,
                type: AiIdeaType.COLOR,
                hex: getColorValue(element),
                text: element.content?.caption ? draftToMarkdown(element.content?.caption) : '',
            };
        case ElementType.IMAGE_TYPE:
        case ElementType.FILE_TYPE:
        case ElementType.LINK_TYPE:
        case ElementType.SKETCH_TYPE:
            return {
                id,
                type: AiIdeaType.IDEA,
                text: element.content?.caption ? draftToMarkdown(element.content?.caption) : '',
            };
        case ElementType.BOARD_TYPE:
        case ElementType.COLUMN_TYPE:
        case ElementType.TASK_LIST_TYPE:
            return {
                id,
                type: AiIdeaType.IDEA,
                text: element.content?.title ? `## ${element.content?.title}` : '',
            };
        case ElementType.CARD_TYPE:
        case ElementType.DOCUMENT_TYPE:
        case ElementType.TASK_TYPE:
            return {
                id,
                type: AiIdeaType.IDEA,
                text: element.content?.textContent ? draftToMarkdown(element.content?.textContent) : '',
            };
        default:
            return;
    }
};
